import { Body, HelioState, HelioVector, RotationAxis, Rotation_EQJ_ECL } from 'astronomy-engine';
import { hermiteAt } from './hermite';
import { tdbDaysToAstroTime } from './time';
import { AU_KM } from './units';
import { type Vec3, cross, set, vec3 } from './vec3';

/** Bodies whose positions come from astronomy-engine (Concept §5). */
export const EPHEMERIS_BODIES = [
  'sun',
  'mercury',
  'venus',
  'earth',
  'mars',
  'jupiter',
  'saturn',
  'uranus',
  'neptune',
  'pluto',
] as const;

export type EphemerisBodyId = (typeof EPHEMERIS_BODIES)[number];

const ASTRONOMY_BODY: Record<EphemerisBodyId, Body> = {
  sun: Body.Sun,
  mercury: Body.Mercury,
  venus: Body.Venus,
  earth: Body.Earth,
  mars: Body.Mars,
  jupiter: Body.Jupiter,
  saturn: Body.Saturn,
  uranus: Body.Uranus,
  neptune: Body.Neptune,
  pluto: Body.Pluto,
};

/** Sidereal orbital periods in days (for orbit lines). */
export const ORBITAL_PERIOD_DAYS: Record<Exclude<EphemerisBodyId, 'sun'>, number> = {
  mercury: 87.969,
  venus: 224.701,
  earth: 365.256,
  mars: 686.98,
  jupiter: 4332.59,
  saturn: 10_759.22,
  uranus: 30_688.5,
  neptune: 60_195,
  pluto: 90_560,
};

/** Perihelion and aphelion distances in AU (rounded outwards), for cheap "could p be near this body?" tests. */
export const ORBIT_RANGE_AU: Record<Exclude<EphemerisBodyId, 'sun'>, readonly [number, number]> = {
  mercury: [0.3, 0.47],
  venus: [0.71, 0.73],
  earth: [0.98, 1.02],
  mars: [1.38, 1.67],
  jupiter: [4.94, 5.46],
  saturn: [9.0, 10.1],
  uranus: [18.2, 20.1],
  neptune: [29.7, 30.4],
  pluto: [29.6, 49.4],
};

export function isEphemerisBody(id: string): id is EphemerisBodyId {
  return (EPHEMERIS_BODIES as readonly string[]).includes(id);
}

// Equatorial J2000 (EQJ) → ecliptic J2000 (ECL), the frame HORIZONS uses with REF_PLANE=ECLIPTIC (decision D3).
const R = Rotation_EQJ_ECL().rot;
const r00 = R[0]![0]!,
  r01 = R[1]![0]!,
  r02 = R[2]![0]!;
const r10 = R[0]![1]!,
  r11 = R[1]![1]!,
  r12 = R[2]![1]!;
const r20 = R[0]![2]!,
  r21 = R[1]![2]!,
  r22 = R[2]![2]!;

/** Rotates an ecliptic J2000 vector into EQJ and scales it (the inverse of eqjToEcliptic; the rotation is orthonormal). */
export function eclipticToEqj(out: Vec3, x: number, y: number, z: number, factor = 1): Vec3 {
  return set(
    out,
    (r00 * x + r10 * y + r20 * z) * factor,
    (r01 * x + r11 * y + r21 * z) * factor,
    (r02 * x + r12 * y + r22 * z) * factor,
  );
}

/** Rotates an EQJ vector into the ecliptic J2000 frame and scales it (in place). */
export function eqjToEcliptic(out: Vec3, x: number, y: number, z: number, factor = 1): Vec3 {
  return set(
    out,
    (r00 * x + r01 * y + r02 * z) * factor,
    (r10 * x + r11 * y + r12 * z) * factor,
    (r20 * x + r21 * y + r22 * z) * factor,
  );
}

/** Heliocentric position of a body in km, ecliptic J2000, at `tdbDays` (days since J2000 TDB). */
export function bodyHelio(id: EphemerisBodyId, tdbDays: number, out: Vec3 = vec3()): Vec3 {
  if (id === 'sun') return set(out, 0, 0, 0);
  const v = HelioVector(ASTRONOMY_BODY[id], tdbDaysToAstroTime(tdbDays));
  return eqjToEcliptic(out, v.x, v.y, v.z, AU_KM);
}

/**
 * Per-frame ephemeris cache: each body is evaluated at most once per distinct time. Returned vectors are owned by
 * the cache and overwritten on the next time change.
 */
export class EphemerisCache {
  private t = Number.NaN;
  private readonly positions = new Map<EphemerisBodyId, Vec3>();
  private readonly valid = new Set<EphemerisBodyId>();

  get(id: EphemerisBodyId, tdbDays: number): Vec3 {
    if (tdbDays !== this.t) {
      this.t = tdbDays;
      this.valid.clear();
    }
    let p = this.positions.get(id);
    if (!p) {
      p = vec3();
      this.positions.set(id, p);
    }
    if (!this.valid.has(id)) {
      bodyHelio(id, tdbDays, p);
      this.valid.add(id);
    }
    return p;
  }
}

/**
 * A body's heliocentric track for bulk lookups (trajectory line sampling, S3-12): cubic Hermite between nodes
 * (position and velocity from astronomy-engine), each node computed once. Error < 2 km for every planet (finer
 * nodes for the inner planets), far below anything visible, at a fraction of the cost of one ephemeris evaluation per lookup. The render
 * loop keeps using the exact `EphemerisCache`.
 */
/**
 * BodyTrack node spacing per body, days: the widest grid that keeps the Hermite curve within 0.2 km of the ephemeris
 * (S4-15, measured over 1975–2025). Mercury moves fastest along the most curved orbit; Earth wobbles around the
 * Earth–Moon barycentre every 27 days; Pluto's own ephemeris is piecewise, so its error grows with the step (1.6 km at
 * one day, 13 km at eight). The outer planets were on a daily grid: the trails of the start sampled up to five of
 * them per sample, most of the trails' cost.
 */
const TRACK_STEP_DAYS: Record<EphemerisBodyId, number> = {
  sun: 1,
  mercury: 0.25,
  venus: 1,
  earth: 0.5,
  mars: 2,
  jupiter: 8,
  saturn: 16,
  uranus: 32,
  neptune: 32,
  pluto: 1,
};

/**
 * Coarse tracks of the outer planets for a quick "is it anywhere near?" test (S4-15): 32-day nodes, within this many
 * km of the ephemeris (Pluto 56 km, Jupiter 14 km). The inner planets have no coarse track.
 */
export const COARSE_TRACK_STEP_DAYS = 32;
export const COARSE_TRACK_ERROR_KM = 100;
export const COARSE_TRACK_BODIES: ReadonlySet<EphemerisBodyId> = new Set([
  'jupiter',
  'saturn',
  'uranus',
  'neptune',
  'pluto',
]);

/**
 * Pluto's mean orbit, J2000 ecliptic: JPL's approximate Keplerian elements for 1800–2050 (Standish, "Keplerian
 * Elements for Approximate Positions of the Major Planets", table 1) — a in AU, e, I, L, ϖ, Ω in degrees, and their
 * rates per Julian century.
 */
const PLUTO_ELEMENTS = [39.48211675, 0.2488273, 17.14001206, 238.92903833, 224.06891629, 110.30393684] as const;
const PLUTO_RATES = [-0.00031596, 0.0000517, 0.00004818, 145.20780515, -0.04062942, -0.01183482] as const;

/** How far plutoKepler may be from the ephemeris, km (S4-03 part 6: 1.44 million km over 1950–2050, with margin). */
export const PLUTO_KEPLER_ERROR_KM = 2.5e6;

/**
 * Pluto's heliocentric position from its mean Keplerian orbit, km, ecliptic J2000: a rough position (within
 * PLUTO_KEPLER_ERROR_KM) for "could a sample be near Pluto?" tests. The ephemeris integrates an 80-year segment on
 * first use, a 60–80 ms task at 4× CPU (S4-03 part 6).
 */
export function plutoKepler(tdbDays: number, out: Vec3 = vec3()): Vec3 {
  const c = tdbDays / 36525;
  const el = (k: number): number => PLUTO_ELEMENTS[k]! + PLUTO_RATES[k]! * c;
  const deg = Math.PI / 180;
  const a = el(0) * AU_KM;
  const e = el(1);
  const inc = el(2) * deg;
  const peri = el(4) * deg;
  const node = el(5) * deg;
  const w = peri - node;
  let m = (el(3) * deg - peri) % (2 * Math.PI);
  if (m > Math.PI) m -= 2 * Math.PI;
  if (m < -Math.PI) m += 2 * Math.PI;
  let ea = m + e * Math.sin(m);
  for (let i = 0; i < 8; i++) ea -= (ea - e * Math.sin(ea) - m) / (1 - e * Math.cos(ea));
  const xp = a * (Math.cos(ea) - e);
  const yp = a * Math.sqrt(1 - e * e) * Math.sin(ea);
  const cw = Math.cos(w);
  const sw = Math.sin(w);
  const cn = Math.cos(node);
  const sn = Math.sin(node);
  const ci = Math.cos(inc);
  const si = Math.sin(inc);
  return set(
    out,
    (cw * cn - sw * sn * ci) * xp + (-sw * cn - cw * sn * ci) * yp,
    (cw * sn + sw * cn * ci) * xp + (-sw * sn + cw * cn * ci) * yp,
    sw * si * xp + cw * si * yp,
  );
}

export class BodyTrack {
  private readonly nodes = new Map<number, Float64Array>();
  private readonly t = [0, 0];
  private readonly s = new Float64Array(12);

  /** Node spacing, days (TRACK_STEP_DAYS). */
  private readonly stepDays: number;

  constructor(
    readonly id: EphemerisBodyId,
    stepDays = TRACK_STEP_DAYS[id],
  ) {
    this.stepDays = stepDays;
  }

  at(tdb: number, out: Vec3): Vec3 {
    if (this.id === 'sun') return set(out, 0, 0, 0);
    const k = Math.floor(tdb / this.stepDays);
    this.s.set(this.node(k), 0);
    this.s.set(this.node(k + 1), 6);
    this.t[0] = k * this.stepDays;
    this.t[1] = (k + 1) * this.stepDays;
    hermiteAt(this.t, this.s, 0, tdb, out);
    return out;
  }

  private node(k: number): Float64Array {
    let n = this.nodes.get(k);
    if (n) return n;
    if (this.nodes.size > 40_000) this.nodes.clear(); // decades of nodes; long jumps start afresh
    const st = HelioState(ASTRONOMY_BODY[this.id], tdbDaysToAstroTime(k * this.stepDays));
    const p = eqjToEcliptic(vec3(), st.x, st.y, st.z, AU_KM);
    const v = eqjToEcliptic(vec3(), st.vx, st.vy, st.vz, AU_KM);
    n = Float64Array.of(p.x, p.y, p.z, v.x, v.y, v.z);
    this.nodes.set(k, n);
    return n;
  }
}

/**
 * Samples one orbital period of a body centred on `tCenter` (days since J2000 TDB). Returns `n` positions as a flat
 * array `[x0, y0, z0, x1, …]` in km. The curve is open; consumers close it.
 */
export function orbitPoints(id: Exclude<EphemerisBodyId, 'sun'>, tCenter: number, n = 256): Float64Array {
  const period = ORBITAL_PERIOD_DAYS[id];
  const out = new Float64Array(3 * n);
  const p = vec3();
  for (let i = 0; i < n; i++) {
    bodyHelio(id, tCenter - period / 2 + (period * i) / n, p);
    out[3 * i] = p.x;
    out[3 * i + 1] = p.y;
    out[3 * i + 2] = p.z;
  }
  return out;
}

/** Bodies with an IAU rotation model in astronomy-engine: the ephemeris bodies and the Moon (S4-07). */
export type RotatingBodyId = EphemerisBodyId | 'moon';

const ROTATION_BODY: Record<RotatingBodyId, Body> = { ...ASTRONOMY_BODY, moon: Body.Moon };

export interface BodyOrientation {
  /** Unit vector of the north pole, ecliptic J2000. */
  north: Vec3;
  /** Unit vector from the centre through the prime meridian on the equator, ecliptic J2000. */
  prime: Vec3;
}

/**
 * Orientation of a body (IAU WGCCRE 2015 via astronomy-engine): north pole and prime meridian. The prime meridian
 * angle W is measured eastwards along the body's equator from the node Q at right ascension α0 + 90°.
 */
export function bodyOrientation(id: RotatingBodyId, tdbDays: number, out?: BodyOrientation): BodyOrientation {
  const o = out ?? { north: vec3(), prime: vec3() };
  const axis = RotationAxis(ROTATION_BODY[id], tdbDaysToAstroTime(tdbDays));
  const n = vec3(axis.north.x, axis.north.y, axis.north.z);
  // astronomy-engine reports Earth's ra from the (nearly polar) axis vector, where it is ill-conditioned; the IAU
  // value is α0 = −0.641°·T. For all other bodies `ra` (hours) is the IAU α0.
  const alpha0 = id === 'earth' ? (-0.641 * tdbDays) / 36525 : axis.ra * 15;
  const a = (alpha0 * Math.PI) / 180;
  const q = vec3(-Math.sin(a), Math.cos(a), 0);
  // make Q exactly perpendicular to the pole (it lies in the body's equator)
  const qn = q.x * n.x + q.y * n.y;
  q.x -= qn * n.x;
  q.y -= qn * n.y;
  q.z -= qn * n.z;
  const ql = Math.hypot(q.x, q.y, q.z);
  q.x /= ql;
  q.y /= ql;
  q.z /= ql;
  const e = cross(vec3(), n, q);
  const w = (axis.spin * Math.PI) / 180;
  const c = Math.cos(w);
  const s = Math.sin(w);
  eqjToEcliptic(o.north, n.x, n.y, n.z);
  eqjToEcliptic(o.prime, c * q.x + s * e.x, c * q.y + s * e.y, c * q.z + s * e.z);
  return o;
}
